<script setup lang="ts">
import type { Interval } from 'date-fns';
import { DurationPriceButton } from '@/features/cart';
import { useVModel } from '@vueuse/core';
import { SlotTimepicker } from './SlotTimepicker';

export type AppointmentScreenResultData = {
  dateTime: Date;
  note?: string;
};

const props = withDefaults(
  defineProps<{
    price: number;
    duration: number;
    appointablePeriods?: Interval[];
    activeDate: Date;
    isLoading?: boolean;
  }>(),
  {
    appointablePeriods: () => [],
    isLoading: false,
  }
);
const emit = defineEmits<{
  (e: 'appoint', data: AppointmentScreenResultData): void;
}>();

const activeDate = useVModel(props, 'activeDate');
const note = ref('');
const selectedDateTime = ref<Date>();

watch(activeDate, () => {
  selectedDateTime.value = undefined;
});

function appoint() {
  if (!selectedDateTime.value) return;
  emit('appoint', {
    dateTime: selectedDateTime.value,
    note: note.value !== '' ? note.value : undefined,
  });
}
</script>

<template>
  <div class="appointment-screen">
    <div class="appointment-screen__title">Дата и время</div>
    <AppDatepicker
      v-model="activeDate"
      class="appointment-screen__datepicker"
    />
    <SlotTimepicker
      v-model="selectedDateTime"
      group-by-daytime
      :date="activeDate"
      :whitelisted-intervals="props.appointablePeriods"
    />
    <AppInput
      v-model="note"
      multiline
      placeholder="Примечание (необязательно)"
      :rows="3"
    />
    <DurationPriceButton
      :price="props.price"
      :duration="props.duration"
      :disabled="!selectedDateTime"
      :loading="props.isLoading"
      @click="appoint"
    >
      Записаться
    </DurationPriceButton>
  </div>
</template>

<style scoped lang="scss">
.appointment-screen {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 24px;

  &__title {
    font-size: 32px;
    font-weight: 700;
  }

  &__datepicker {
    background-color: var(--app-color-white-secondary);
    padding: 16px;
    border-radius: 12px;
  }
}
</style>
